import { createContext, useContext, useEffect, type ReactNode } from 'react';

// Ported from prototype/src/seoHead.js. Pages call useSeo(spec). On the server the spec is captured
// during render and turned into <head> tags (brief §1: crawlers see title, description, canonical,
// OG and JSON-LD without JavaScript). In the browser the same spec updates document.head.

export interface PreloadImageSpec {
  href: string;
  imagesrcset?: string | undefined;
  imagesizes?: string | undefined;
  type?: string | undefined;
}

export interface SeoSpec {
  title: string;
  description?: string | undefined;
  canonical?: string | undefined;
  robots?: string | undefined; // default "index,follow"
  image?: string | undefined;
  type?: string | undefined; // og:type, default "website"
  jsonLd?: object | undefined;
  // The page's likely LCP image (e.g. a large above-the-fold illustration): preloaded so the
  // browser starts fetching it immediately instead of discovering it only once React hydrates.
  preloadImage?: PreloadImageSpec | undefined;
}

export interface HeadCollector {
  spec: SeoSpec | null;
}

const HeadContext = createContext<HeadCollector | null>(null);

export function HeadProvider({
  collector,
  children,
}: {
  collector: HeadCollector | null;
  children: ReactNode;
}) {
  return <HeadContext.Provider value={collector}>{children}</HeadContext.Provider>;
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// JSON inside <script>: "<" is escaped so "</script>" in any value can't end the element early.
export function jsonForScript(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export function headHtml(spec: SeoSpec | null): string {
  if (!spec) return '';
  const robots = spec.robots ?? 'index,follow';
  const tags: string[] = [`<title>${escapeAttr(spec.title)}</title>`];
  const meta = (attr: 'name' | 'property', key: string, content: string | undefined) => {
    if (content) tags.push(`<meta ${attr}="${key}" content="${escapeAttr(content)}" />`);
  };
  meta('name', 'description', spec.description);
  meta('name', 'robots', robots);
  meta('property', 'og:title', spec.title);
  meta('property', 'og:description', spec.description);
  meta('property', 'og:type', spec.type ?? 'website');
  meta('property', 'og:url', spec.canonical);
  meta('property', 'og:image', spec.image);
  meta('name', 'twitter:card', 'summary_large_image');
  meta('name', 'twitter:title', spec.title);
  meta('name', 'twitter:description', spec.description);
  if (spec.canonical) tags.push(`<link rel="canonical" href="${escapeAttr(spec.canonical)}" />`);
  if (spec.preloadImage) {
    const { href, imagesrcset, imagesizes, type } = spec.preloadImage;
    const attrs = [
      `rel="preload"`,
      `as="image"`,
      `href="${escapeAttr(href)}"`,
      imagesrcset ? `imagesrcset="${escapeAttr(imagesrcset)}"` : '',
      imagesizes ? `imagesizes="${escapeAttr(imagesizes)}"` : '',
      type ? `type="${escapeAttr(type)}"` : '',
    ].filter(Boolean);
    tags.push(`<link ${attrs.join(' ')} />`);
  }
  if (spec.jsonLd) {
    tags.push(
      `<script type="application/ld+json" id="jsonld">${jsonForScript(spec.jsonLd)}</script>`,
    );
  }
  return tags.join('\n    ');
}

function setMeta(attr: 'name' | 'property', key: string, content: string | undefined): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function useSeo(spec: SeoSpec): void {
  const collector = useContext(HeadContext);
  // Server render: capture synchronously (effects don't run during renderToString).
  if (collector) collector.spec = spec;

  const ld = JSON.stringify(spec.jsonLd ?? null);
  const preloadKey = JSON.stringify(spec.preloadImage ?? null);
  useEffect(() => {
    document.title = spec.title;
    setMeta('name', 'description', spec.description);
    setMeta('name', 'robots', spec.robots ?? 'index,follow');
    setMeta('property', 'og:title', spec.title);
    setMeta('property', 'og:description', spec.description);
    setMeta('property', 'og:type', spec.type ?? 'website');
    setMeta('property', 'og:url', spec.canonical);
    setMeta('property', 'og:image', spec.image);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', spec.title);
    setMeta('name', 'twitter:description', spec.description);
    let preload = document.head.querySelector<HTMLLinkElement>('link[rel="preload"][as="image"]');
    if (spec.preloadImage) {
      if (!preload) {
        preload = document.createElement('link');
        preload.rel = 'preload';
        preload.setAttribute('as', 'image');
        document.head.appendChild(preload);
      }
      preload.href = spec.preloadImage.href;
      if (spec.preloadImage.imagesrcset) {
        preload.setAttribute('imagesrcset', spec.preloadImage.imagesrcset);
      }
      if (spec.preloadImage.imagesizes) {
        preload.setAttribute('imagesizes', spec.preloadImage.imagesizes);
      }
      if (spec.preloadImage.type) preload.type = spec.preloadImage.type;
    } else {
      preload?.remove();
    }
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (spec.canonical) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = spec.canonical;
    } else {
      link?.remove();
    }
    let script = document.getElementById('jsonld');
    if (spec.jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.id = 'jsonld';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(spec.jsonLd);
    } else {
      script?.remove();
    }
    // `ld`/`preloadKey` stand in for spec.jsonLd/spec.preloadImage so a new object with the same
    // content doesn't re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spec.title,
    spec.description,
    spec.canonical,
    spec.robots,
    spec.image,
    spec.type,
    ld,
    preloadKey,
  ]);
}
